import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Wrench } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CoverImg({ src, alt, className, icon: FallbackIcon }) {
  const [err, setErr] = useState(false);
  if (err || !src) {
    return (
      <div className={`${className} bg-ink-900/5 flex items-center justify-center text-ink/25`}>
        <FallbackIcon size={34} />
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setErr(true)} className={`${className} object-cover`} />;
}

function SectionHead({ eyebrow, title, linkTo, linkLabel, dark }) {
  return (
    <Reveal>
      <div className={`flex flex-wrap items-end justify-between gap-6 border-b pb-5 ${dark ? "border-paper/15" : "hairline"}`}>
        <div>
          <span className="label-mono text-gold">{eyebrow}</span>
          <h2 className="font-display text-3xl md:text-5xl font-semibold mt-3 tracking-tight max-w-3xl">{title}</h2>
        </div>
        <Link to={linkTo} data-testid={`showcase-link-${linkTo.slice(1)}`}
          className={`group inline-flex items-center gap-2 label-mono link-underline ${dark ? "text-paper/80" : "text-ink"}`}>
          {linkLabel} <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform" />
        </Link>
      </div>
    </Reveal>
  );
}

export default function ProfileShowcase({ books, tools, formations }) {
  return (
    <>
      {books.length > 0 && (
        <section id="publications" className="bg-paper py-24 md:py-32" data-testid="profile-publications">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <SectionHead eyebrow="06 — Publications & livres" title="Publications de Majdi Zarougui" linkTo="/livres" linkLabel="Tous les livres" />
            <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {books.map((b) => (
                <StaggerItem key={b.id}>
                  <article className="group h-full flex flex-col rounded-sm overflow-hidden border hairline bg-white hover:shadow-[0_20px_50px_-20px_rgba(10,26,51,0.3)] transition-shadow duration-500" data-testid={`pub-${b.id}`}>
                    <CoverImg src={b.has_cover ? `${API}/books/${b.id}/cover` : null} alt={`Couverture — ${b.title}`} icon={BookOpen} className="w-full h-64" />
                    <div className="flex flex-col flex-1 p-7">
                      {b.category && <span className="label-mono text-gold">{b.category}</span>}
                      <h3 className="font-display text-xl font-semibold leading-snug mt-2">{b.title}</h3>
                      {b.description && <p className="text-ink/55 text-sm mt-3 flex-1 leading-relaxed line-clamp-3">{b.description}</p>}
                      <div className="flex items-center gap-3 mt-6 pt-5 border-t hairline">
                        <Link to="/livres" data-testid={`pub-discover-${b.id}`} className="label-mono text-ink link-underline inline-flex items-center gap-1.5">Découvrir <ArrowUpRight size={14} /></Link>
                        {b.buy_url && (
                          <a href={b.buy_url} target="_blank" rel="noopener noreferrer" data-testid={`pub-buy-${b.id}`}
                            className="ml-auto inline-flex items-center gap-2 rounded-full bg-ink text-paper px-4 py-2 label-mono hover:bg-gold hover:text-ink transition-colors">
                            Acheter{b.price ? ` · ${b.price} €` : ""}
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {tools.length > 0 && (
        <section id="outils" className="bg-ink-900 text-paper grain relative py-24 md:py-32" data-testid="profile-tools">
          <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
            <SectionHead dark eyebrow="07 — Outils & applications" title="Outils Lean développés par Majdi Zarougui" linkTo="/outils-apps" linkLabel="Tous les outils" />
            <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {tools.map((t) => (
                <StaggerItem key={t.slug}>
                  <article className="group h-full flex flex-col rounded-sm overflow-hidden border border-paper/10 bg-ink-950/70 hover:border-gold/50 transition-colors duration-500" data-testid={`tool-${t.slug}`}>
                    <CoverImg src={t.has_cover ? `${API}/tools/${t.slug}/cover` : null} alt={`Aperçu — ${t.name}`} icon={Wrench} className="w-full h-52" />
                    <div className="flex flex-col flex-1 p-7">
                      <div className="flex items-center gap-3 label-mono">
                        <span className="text-gold">{t.category}</span>
                        <span className={t.free ? "text-emerald-400" : "text-paper/60"}>{t.free ? "Gratuit" : `${t.price} €`}</span>
                      </div>
                      <h3 className="font-display text-xl font-semibold leading-snug mt-3">{t.name}</h3>
                      {t.short && <p className="text-paper/55 text-sm mt-3 flex-1 leading-relaxed line-clamp-3">{t.short}</p>}
                      <Link to={`/outils-apps/${t.slug}`} data-testid={`tool-discover-${t.slug}`}
                        className="group/btn inline-flex items-center gap-2 mt-6 label-mono text-paper link-underline self-start">
                        Découvrir <ArrowUpRight size={14} className="group-hover/btn:rotate-45 transition-transform" />
                      </Link>
                    </div>
                  </article>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {formations.length > 0 && (
        <section id="formations" className="bg-paper py-24 md:py-32" data-testid="profile-formations">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <SectionHead eyebrow="08 — Formations" title="Transmettre les méthodes du terrain." linkTo="/formation" linkLabel="Toutes les formations" />
            <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {formations.map((f) => (
                <StaggerItem key={f.lookup}>
                  <Link to="/formation" data-testid={`profile-formation-${f.lookup}`}
                    className="group h-full flex flex-col rounded-sm overflow-hidden border hairline bg-white hover:shadow-[0_20px_50px_-20px_rgba(10,26,51,0.3)] transition-shadow duration-500">
                    <div className="relative h-44 overflow-hidden">
                      <img src={f.image} alt={`Formation ${f.title}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1100ms] ease-out" />
                      <span className="absolute top-4 left-4 label-mono text-paper bg-ink-900/70 backdrop-blur px-3 py-1.5 rounded-full">{f.tag}</span>
                    </div>
                    <div className="flex flex-col flex-1 p-6">
                      <h3 className="font-display text-xl font-semibold leading-snug">{f.title}</h3>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t hairline label-mono text-ink/50">
                        <span>{f.level} · {f.duration}</span>
                        <span className="text-ink font-semibold">{f.price} €</span>
                      </div>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}
    </>
  );
}
